import {FC, memo, useCallback, useEffect, useMemo} from "react";
import {Controller, useForm} from "react-hook-form";
import {Drawer} from "shared/ui/drawer";
import {
  PesticideMapCreatePointBody,
  PesticideMapCreatePointFooter,
  PesticideMapCreatePointHead,
  PesticideMapCreatePointItem,
  PesticideMapCreatePointItemHead,
  PesticideMapCreatePointTitle,
  PesticideMapCreatePointWrapper
} from "./styles";
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import {CloseIcon} from "shared/ui/icons";
import {toRem} from "shared/helpers";
import {Spacer} from "shared/ui/spacer";
import InputDate from '@atomaro/ui-kit/components/InputDate/InputDate';
import {withMask} from "use-mask-input";
import Select from '@atomaro/ui-kit/components/Select/Select';
import Multiselect from '@atomaro/ui-kit/components/Multiselect/Multiselect';
import {SearchList} from "shared/ui/searchList";
import {IDropdownMenuItem} from "@atomaro/ui-kit/components/DropdownMenu/types";
import {usePesticideMapStore} from "entities/pesticideMap";
import {useFiltersStore} from "../lib/store";
import {regions as regionsObj} from "shared/consts";
import {FormValues} from "../types/types";

const STATUS_ITEMS: IDropdownMenuItem[] = [
  { key: 'actual', value: 'Применение планируется' },
  { key: 'actualUpdate', value: 'Применение изменено' },
  { key: 'done', value: 'Применение выполнено' },
  { key: 'canceled', value: 'Применение отменено' },
];

const HUMAN_HAZARD_ITEMS: IDropdownMenuItem[] = [
  { key: '1', value: 'Вещества чрезвычайно опасные' },
  { key: '2', value: 'Вещества высокоопасные' },
  { key: '3', value: 'Вещества умеренно опасные' },
  { key: '4', value: 'Вещества малоопасные' },
  { key: '0', value: 'Вещества неопасные' },
];

const BEE_HAZARD_ITEMS: IDropdownMenuItem[] = [
  { key: '1', value: 'Высокоопасные' },
  { key: '2', value: 'Среднеопасные' },
  { key: '3', value: 'Малоопасные' },
  { key: '0', value: 'Неопасные' },
];

const SORTED_REGIONS = [...regionsObj].sort((a, b) => a.name.localeCompare(b.name));
const REGIONS_ITEMS_STATIC = SORTED_REGIONS.map(r => ({ name: r.name, id: `${r.id}` }));

export const PesticideMapFilters: FC<{
  isOpen: boolean;
  isMobile?: boolean;
  onClose: () => void;
  onFiltersSubmit: (data: FormValues) => void;
}> = memo(({ isOpen, isMobile, onClose, onFiltersSubmit }) => {
  const { filters, setFilters } = useFiltersStore();

  const {
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    resetField,
  } = useForm<FormValues>({
    defaultValues: filters,
  });

  useEffect(() => {
    if (isOpen) {
      reset(filters);
    }
  }, [isOpen, filters, reset]);

  const regionName = watch('regionName');
  const districtName = watch('districtName');

  const regions = useMemo(() => {
    return REGIONS_ITEMS_STATIC.map(r => ({
      ...r,
      isSelected: regionName === r.id,
    }));
  }, [regionName]);

  const districtsFull = usePesticideMapStore(s => s.districts);
  const districts = useMemo(() => {
    if (!regionName) return [];
    return districtsFull.map(d => ({
      id: `${d.id}`,
      name: d.name,
      isSelected: districtName === `${d.id}`,
    }));
  }, [districtsFull, regionName, districtName]);

  const getDistrictsFromServer = usePesticideMapStore(s => s.getDistrictsFromServer);

  const handleRegionClick = useCallback((id: string) => {
    setValue('regionName', id, { shouldValidate: false });
    setValue('districtName', null, { shouldValidate: false });
    getDistrictsFromServer(id);
  }, [setValue, getDistrictsFromServer]);

  const handleDistrictClick = useCallback((id: string) => {
    setValue('districtName', id, { shouldValidate: false });
  }, [setValue]);

  const handleResetField = useCallback((field: keyof FormValues) => {
    if (field === 'dates') {
      setValue('dates', { from: null, to: null }, { shouldValidate: false });
      return;
    }
    resetField(field as any);
  }, [resetField, setValue]);

  const handleResetRegion = useCallback(() => {
    setValue('regionName', null, { shouldValidate: false });
    setValue('districtName', null, { shouldValidate: false });
  }, [setValue]);

  const handleSubmitForm = useCallback((data: FormValues) => {

  const prepared = {
    ...data,
    dates: {
      from: data.dates.from ? new Date(data.dates.from) : null,
      to: data.dates.to ? new Date(data.dates.to) : null,
    },
  };


  setFilters(prepared);
  onFiltersSubmit(prepared);
  onClose();
}, [setFilters, onFiltersSubmit, onClose]);

  return (
    <Drawer isOpen={isOpen} position="right" onClose={onClose}>
      <PesticideMapCreatePointWrapper onSubmit={handleSubmit(handleSubmitForm)}>
        <PesticideMapCreatePointHead $isMobile={isMobile}>
          <Button
            iconPrefix={<CloseIcon />}
            variant={isMobile ? 'ghost' : 'secondary'}
            colorScheme="neutral"
            style={{ alignSelf: 'flex-end', width: isMobile ? toRem(24) : toRem(36) }}
            size={isMobile ? 's' : 'm'}
            onClick={onClose}
          />
          <PesticideMapCreatePointTitle>Фильтры</PesticideMapCreatePointTitle>
          {isMobile && <Spacer width={toRem(24)} height={toRem(24)} />}
        </PesticideMapCreatePointHead>

        <PesticideMapCreatePointBody>
          <Controller
            control={control}
            name="dates"
            render={({ field }) => (
              <PesticideMapCreatePointItem>
                <PesticideMapCreatePointItemHead>
                  <h3>Период применения</h3>
                  {(field.value?.from || field.value?.to) &&
                    <a onClick={() => handleResetField('dates')}>Сбросить</a>
                  }
                </PesticideMapCreatePointItemHead>
               <Controller
                  control={control}
                  name="dates"
                  render={({ field }) => (
                    <InputDate
                      isRange
                      useInPortal={false}
                      ref={withMask(['99.99.9999', '99.99.9999 - 99.99.9999'])}
                      placeholder="Выберите даты"
                      activeDate={field.value?.from ? new Date(field.value.from) : undefined}
                      secondDate={field.value?.to ? new Date(field.value.to) : undefined}
                      onChange={(from, to) => field.onChange({ from, to })}
                    />
                  )}
                />
              </PesticideMapCreatePointItem>
            )}
          />

          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <PesticideMapCreatePointItem>
                <PesticideMapCreatePointItemHead>
                  <h3>Статус работ</h3>
                  {field.value && <a onClick={() => setValue('status', null)}>Сбросить</a>}
                </PesticideMapCreatePointItemHead>
                <Select
                  placeholder="Выберите данные"
                  name={field.name}
                  value={field.value}
                  placement="bottom"
                  useInPortal={false}
                  items={STATUS_ITEMS}
                  onChange={field.onChange}
                />
              </PesticideMapCreatePointItem>
            )}
          />

          <Controller
            control={control}
            name="regionName"
            render={({ field }) => (
              <PesticideMapCreatePointItem>
                <PesticideMapCreatePointItemHead>
                  <h3>Регион</h3>
                  {field.value && <a onClick={handleResetRegion}>Сбросить</a>}
                </PesticideMapCreatePointItemHead>
                <SearchList
                  isDrawer
                  items={regions}
                  onItemClick={handleRegionClick}
                />
              </PesticideMapCreatePointItem>
            )}
          />

          {districts.length > 0 && (
            <Controller
              control={control}
              name="districtName"
              render={({ field }) => (
                <PesticideMapCreatePointItem>
                  <PesticideMapCreatePointItemHead>
                    <h3>Район</h3>
                    {field.value && <a onClick={() => handleResetField('districtName')}>Сбросить</a>}
                  </PesticideMapCreatePointItemHead>
                  <SearchList
                    isDrawer
                    items={districts}
                    onItemClick={handleDistrictClick}
                  />
                </PesticideMapCreatePointItem>
              )}
            />
          )}

          <Controller
            control={control}
            name="humanHazardClass"
            render={({ field }) => (
              <PesticideMapCreatePointItem>
                <PesticideMapCreatePointItemHead>
                  <h3>Класс опасности для человека</h3>
                  {field.value?.length > 0 && <a onClick={() => setValue('humanHazardClass',[])}>Сбросить</a>}
                </PesticideMapCreatePointItemHead>
                <Multiselect
                  placeholder="Выберите данные"
                  name={field.name}
                  value={field.value}
                  placement="bottom"
                  useInPortal={false}
                  items={HUMAN_HAZARD_ITEMS}
                  onChange={field.onChange}
                  size="m"
                />
              </PesticideMapCreatePointItem>
            )}
          />

          <Controller
            control={control}
            name="beeHazardClass"
            render={({ field }) => (
              <PesticideMapCreatePointItem>
                <PesticideMapCreatePointItemHead>
                  <h3>Класс опасности для пчел</h3>
                  {field.value?.length > 0 && <a onClick={() => setValue('beeHazardClass', [])}>Сбросить</a>}
                </PesticideMapCreatePointItemHead>
                <Multiselect
                  placeholder="Выберите данные"
                  name={field.name}
                  value={field.value}
                  placement="bottom"
                  useInPortal={false}
                  items={BEE_HAZARD_ITEMS}
                  onChange={field.onChange}
                  size="m"
                />
              </PesticideMapCreatePointItem>
            )}
          />

        </PesticideMapCreatePointBody>

        <PesticideMapCreatePointFooter>
          <Button label="Применить" type="submit" />
        </PesticideMapCreatePointFooter>
      </PesticideMapCreatePointWrapper>
    </Drawer>
  );
});

PesticideMapFilters.displayName = 'PesticideMapFilters';
export default PesticideMapFilters;
